import { useEffect, useState } from 'react';
import { gridPageMaxWidthInPixels } from '../editor/shared/gridConfig';

const BREAKPOINT_SMALL = 600;
const PADDING_SMALL = 8;
const PADDING_LARGE = 24;

function getSidePadding(viewportWidth: number): number {
  return viewportWidth <= BREAKPOINT_SMALL ? PADDING_SMALL : PADDING_LARGE;
}

type MobileZoomStyles = {
  wrapperStyle: React.CSSProperties;
  innerStyle: React.CSSProperties;
  getScaledHeight: (baseHeight: number) => number;
};

export function useMobileZoomStyles(canvasWidth: number = gridPageMaxWidthInPixels): MobileZoomStyles {
  const [state, setState] = useState<{ scale: number; sidePadding: number }>(() => ({
    scale: 1,
    sidePadding: getSidePadding(window.innerWidth),
  }));
  useEffect(() => {
    let raf: number | null = null;

    const visualViewport = window.visualViewport;
    const getViewportWidth = () => visualViewport?.width ?? window.innerWidth;

    const computeScale = (viewportWidth: number) => {
      const totalPadding = getSidePadding(viewportWidth) * 2;

      if (viewportWidth >= canvasWidth + totalPadding) {
        return 1;
      }

      const availableWidth = viewportWidth - totalPadding;
      return availableWidth / canvasWidth;
    };

    const recalc = () => {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const viewportWidth = Math.round(getViewportWidth());
        setState({
          scale: computeScale(viewportWidth),
          sidePadding: getSidePadding(viewportWidth),
        });
      });
    };

    recalc();

    visualViewport?.addEventListener('resize', recalc);
    window.addEventListener('resize', recalc);
    window.addEventListener('orientationchange', recalc);

    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      visualViewport?.removeEventListener('resize', recalc);
      window.removeEventListener('resize', recalc);
      window.removeEventListener('orientationchange', recalc);
    };
  }, [canvasWidth]);

  const { scale, sidePadding } = state;
  const getScaledHeight = (baseHeight: number) => baseHeight * scale;

  if (scale >= 1) return { wrapperStyle: {}, innerStyle: {}, getScaledHeight };

  const wrapperStyle: React.CSSProperties = {
    overflow: 'hidden',
    display: 'grid',
    justifyItems: 'center',
    paddingLeft: `${sidePadding}px`,
    paddingRight: `${sidePadding}px`,
  };

  const widthPct = 100 / scale;

  const innerStyle: React.CSSProperties = {
    transform: `scale(${scale})`,
    transformOrigin: 'top center',
    width: `calc(${widthPct.toFixed(4)}%)`,
    willChange: 'transform',
  };

  return { wrapperStyle, innerStyle, getScaledHeight };
}
